/**
 * RowGroupsDropZone Component
 *
 * Drop zone for row groups with sortable list of grouped fields.
 * Fields in row groups determine how data is grouped in the pivot table.
 */

import { useDroppable } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { TableFieldApiResponse } from '@loader/shared-types';
import { parseFieldId } from '@loader/shared-types';
import { GripVertical, X, ArrowUp, ArrowDown, ArrowUpDown, TableProperties } from 'lucide-react';

import type { LinkedTableCalculatedColumn } from '@/lib/types';
import { findFieldById } from '@/features/linkedTables/editLinkedTable/utils/viewHelpers';

type SortOrder = 'asc' | 'desc' | 'none';

/**
 * Props for RowGroupsDropZone
 */
interface RowGroupsDropZoneProps {
  /** Array of field IDs in row groups (order matters) */
  rowGroups: string[];
  /** All available fields for lookup */
  availableFields: TableFieldApiResponse[];
  /** Calculated columns for lookup */
  calculatedColumns?: LinkedTableCalculatedColumn[];
  /** Handler to remove a field from row groups */
  onRemove?: (fieldId: string) => void;
  /** Handler to reorder row groups */
  onReorder?: (fieldIds: string[]) => void;
  /** Sort orders for each field (optional) */
  sortOrders?: Record<string, SortOrder>;
  /** Handler to update sort order */
  onUpdateSortOrder?: (fieldId: string, order: SortOrder) => void;
  /** Currently dragged item ID (for showing drop indicator) */
  activeId?: string | null;
  /** Item being hovered over (for showing drop indicator) */
  overId?: string | null;
  /** Whether the user can edit (default: true) */
  canEdit?: boolean;
}

/**
 * Individual sortable row group item
 */
interface SortableRowGroupItemProps {
  fieldId: string;
  field: TableFieldApiResponse | undefined;
  sortOrder?: SortOrder;
  onRemove?: (fieldId: string) => void;
  onUpdateSortOrder?: (fieldId: string, order: SortOrder) => void;
  canEdit?: boolean;
}

function SortableRowGroupItem({
  fieldId,
  field,
  sortOrder = 'none',
  onRemove,
  onUpdateSortOrder,
  canEdit = true,
}: SortableRowGroupItemProps) {
  // Use prefixed ID to avoid conflicts with column list items
  const sortableId = `rowgroup-${fieldId}`;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: sortableId,
    disabled: !canEdit,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Parse qualified ID for display fallback (show bare ID, not "alias:id")
  const displayName = field ? field.name : parseFieldId(fieldId).fieldId;

  // Cycle through: none → asc → desc → none
  const handleToggleSortOrder = () => {
    if (!canEdit) return;
    const nextOrder: SortOrder =
      sortOrder === 'none' ? 'asc' :
      sortOrder === 'asc' ? 'desc' :
      'none';
    onUpdateSortOrder?.(fieldId, nextOrder);
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRemove?.(fieldId);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-1.5 rounded-lg bg-accent px-2 py-1.5 ${canEdit ? 'cursor-pointer hover:bg-accent/80' : ''}`}
      onClick={handleToggleSortOrder}
    >
      {/* Drag Handle */}
      {canEdit && (
        <div
          className="cursor-grab active:cursor-grabbing shrink-0"
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </div>
      )}

      {/* Field Name */}
      <span className="flex-1 text-sm truncate" title={displayName}>
        {displayName}
      </span>

      {/* Sort Order Icon */}
      <div className="h-5 w-5 flex items-center justify-center shrink-0 text-muted-foreground">
        {sortOrder === 'asc' && <ArrowUp className="h-3.5 w-3.5" />}
        {sortOrder === 'desc' && <ArrowDown className="h-3.5 w-3.5" />}
        {sortOrder === 'none' && <ArrowUpDown className="h-3.5 w-3.5 opacity-50" />}
      </div>

      {/* Remove Button */}
      {canEdit && (
        <button
          type="button"
          onClick={handleRemove}
          onPointerDown={(e) => e.stopPropagation()}
          className="h-5 w-5 flex items-center justify-center rounded hover:bg-background shrink-0 text-muted-foreground hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

/**
 * Drop placeholder component
 */
function DropPlaceholder() {
  return (
    <div className="h-8 rounded-lg border border-dashed border-primary bg-primary/5 flex items-center justify-center">
      <span className="text-xs text-primary">Drop here</span>
    </div>
  );
}

/**
 * RowGroupsDropZone Component
 */
export function RowGroupsDropZone({
  rowGroups,
  availableFields,
  calculatedColumns = [],
  onRemove,
  sortOrders = {},
  onUpdateSortOrder,
  activeId,
  overId,
  canEdit = true,
}: RowGroupsDropZoneProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: 'row-groups-zone',
  });

  // Determine if we should show the drop placeholder
  // Show when dragging from outside (column list or values) and hovering over this zone
  const isFromOutside = activeId && !activeId.startsWith('rowgroup-');
  const isOverThisZone = overId === 'row-groups-zone' || (overId?.startsWith('rowgroup-') ?? false);
  const showPlaceholder = isFromOutside && isOverThisZone;

  // Helper to find field by ID (supports qualified "alias:fieldId" format)
  const lookupField = (fieldId: string) => findFieldById(fieldId, availableFields, calculatedColumns);

  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
        <TableProperties className="h-4 w-4" />
        <span>Row Groups</span>
      </div>

      {/* Drop Zone */}
      <div
        ref={setNodeRef}
        className={`
          min-h-[36px] rounded-lg p-1.5 border border-dashed
          transition-colors
          ${isOver ? 'bg-primary/10 border-primary' : 'border-border'}
        `}
      >
        {rowGroups.length === 0 ? (
          showPlaceholder ? (
            <DropPlaceholder />
          ) : (
            <div className="flex items-center justify-center h-8 text-sm text-muted-foreground">
              Drag columns here
            </div>
          )
        ) : (
          <SortableContext
            items={rowGroups.map((id) => `rowgroup-${id}`)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-1">
              {rowGroups.map((fieldId) => {
                const field = lookupField(fieldId);
                return (
                  <SortableRowGroupItem
                    key={fieldId}
                    fieldId={fieldId}
                    field={field}
                    sortOrder={sortOrders[fieldId]}
                    onRemove={onRemove}
                    onUpdateSortOrder={onUpdateSortOrder}
                    canEdit={canEdit}
                  />
                );
              })}
              {/* Show placeholder at end when dragging from outside */}
              {showPlaceholder && <DropPlaceholder />}
            </div>
          </SortableContext>
        )}
      </div>
    </div>
  );
}
